import React from 'react';
import {
  useMap,
  AdvancedMarker,
} from '@vis.gl/react-google-maps';
import styles from './WeatherStations.module.css';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import {
  userSelectedWeatherStation,
  showWeatherStationModal,
} from './weatherStations-actions';
import moment from 'moment';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

const WeatherStationsMarkers = ({weatherStations}) => {

  const dispatch = useDispatch();

  const {
    selectedDataPoint1,
    selectedDataPoint2,
    selectedDataPoint3,
  } = useSelector(state => state.weatherStations);

  const map = useMap();

  const [markers, setMarkers] = React.useState({});
  
  const clusterer = React.useRef(null);

  React.useEffect(() => {
    if (!map) {
      return;
    }
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({
        map: map,
        algorithm: new SuperClusterAlgorithm({
          radius: 400,
          maxZoom: 11,
          minZoom: 0,
          minPoints: 1,
        }),
      });
    }
  }, [map]);

  React.useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  const setMarkerRef = (marker, key) => {
    if (marker && markers[key]) {
      return;
    }
    if (!marker && !markers[key]) {
      return;
    }
    setMarkers(prev => {
      if (marker) {
        return {...prev, [key]: marker};
      } 
      else {
        const newMarkers = {...prev};
        delete newMarkers[key];
        return newMarkers;
      }
    });
  };

  const getDataPointUI = (dataPoint, weatherStation) => {
    if (dataPoint === 'temperature') {
      return (
        <div className={styles.dataPoint}>
          <span>Temperature: </span>
          <strong>{weatherStation.temperature} &#8457;</strong>
        </div>
      );
    }
    else if (dataPoint === 'humidity') {
      return (
        <div className={styles.dataPoint}>
          <span>Humidity: </span>
          <strong>{weatherStation.humidity}%</strong>
        </div>
      );
    }
    else if (dataPoint === 'last24HoursRainfall') {
      return (
        <div className={styles.rainfall}>
          <span>Rainfall: </span>
          <strong>{isNaN(weatherStation.rainfall24Hours) || weatherStation.rainfall24Hours === null ? '0' : weatherStation.rainfall24Hours} In</strong>
        </div>
      );
    }
    else if (dataPoint === 'barometer') {
      return (
        <div className={styles.dataPoint}>
          <span>Barometer: </span>
          <strong>{weatherStation.barometerAbsolute} Hg</strong>
        </div>
      );
    }
    else if (dataPoint === 'dewPoint') {
      return (
        <div className={styles.dataPoint}>
          <span>Dew Point: </span>
          <strong>{weatherStation.dewPoint} &#8451;</strong>
        </div>
      );
    }
    else if (dataPoint === 'heatIndex') {
      return (
        <div className={styles.dataPoint}>
          <span>Heat Index: </span>
          <strong>{weatherStation.heatIndex} &#8457;</strong>
        </div>
      );
    }
    else if (dataPoint === 'lastMonthRainfall') {
      return (
        <div className={styles.dataPoint}>
          <span>Rainfall: </span>
          <strong>{isNaN(weatherStation.rainfallLastMonth) || weatherStation.rainfallLastMonth === null ? '0' : weatherStation.rainfallLastMonth} In</strong>
        </div>
      );
    }
    else if (dataPoint === 'windspeed') {
      return (
        <div className={styles.dataPoint}>
          <span>Windspeed: </span>
          <strong>{weatherStation.windSpeed} MPH</strong>
        </div>
      );
    }
    else if (dataPoint === 'windDirection') {
      return (
        <div className={styles.dataPoint}>
          <span>Wind Direction: </span>
          <strong>{weatherStation.windDirection}</strong>
        </div>
      );
    }
    else if (dataPoint === 'soilTemp') {
      return (
        <div className={styles.dataPoint}>
          <span>Soil Temperature: </span>
          <strong>{weatherStation.soilTempAt4Inch === null ? 'N/A' : weatherStation.soilTempAt4Inch.toFixed(2)} &#8457;</strong>
        </div>
      );
    }
    else {
      return null;
    }
  }

  return (
    <>
      {
        weatherStations.map((weatherStation, index) => (
          <AdvancedMarker
            position={{
              lng: weatherStation.longitude,
              lat: weatherStation.latitude
            }}
            key={weatherStation.stationId}
            ref={(marker) => setMarkerRef(marker, weatherStation.id)}
            onClick={() => {
              dispatch(userSelectedWeatherStation(weatherStation, true));
              dispatch(showWeatherStationModal());
            }}>
            <div className={styles.weatherStationWidget}>
              <div className={styles.stationName}>
                {weatherStation.stationName + (moment().diff(moment(weatherStation.observationDateTime), 'days') > 1 ? ' (Offline)' : '')}
              </div>
              <hr className={styles.separator} />
              <div className={styles.weatherInfo}>
                {getDataPointUI(selectedDataPoint1, weatherStation)}
                {getDataPointUI(selectedDataPoint2, weatherStation)}
                {getDataPointUI(selectedDataPoint3, weatherStation)}
              </div>
              <hr className={styles.separator} />
              <div className={styles.weatherInfo}>
              <div className={styles.smallerDataPoint}>
                <span>Last Updated: </span>
                <strong>{weatherStation.observationDateTimeFormatted.replace(' CST', '')}</strong>
              </div>
              </div>
              <div className={styles.arrow}>&#x25BC;</div>
            </div>
          </AdvancedMarker>
        ))
      }
    </>
  );
};

export default WeatherStationsMarkers;
